import { ISearchFilter, ISearchSortFilter } from "../../../types/shared";
import { ALIGN_OPTION_VALUES } from "../components/Credential/Presentation/types/common";
import { TabId } from "../components/List/NavigationTabs";
import { AssignmentTableColumnsKeysType } from "../util/common";
import { IAssigmentsTableDataWithRequiredIds } from "./common";
import { IProject } from "./project";

// Primitive Refs - Im not too sure about having these, I added these since they add some semantic meaning to just "ref"
// and helps in distinguising refs
// TODO: balraj - think about if we need these primitive refs or simply assigning ref as string in the interface would suffice
export type CredRef = string;
export type AssignmentRef = string;
export type IShortlistRef = string;

export interface ICollaborator {
  entity_id: string; // account id
  access: number; // 100 means FullRead and 1000 means Full, Refer enum CollaboratorAccess
}

export interface ReqCxt {
  bool_cxt?: string[];
  company_cxt?: any;
  exec_conditions?: any;
}

export interface IRequirement {
  bool_str: string; // (<mid> OR <kw>) AND (<kw> OR !<mid2>)
  section_order?: number; // 0, 1, 2
  section_name?: string; // Current Role etc.
  section_type: string; // 'MH' or 'NTH' -> Must have or Nice To Have.
  section_id: string; // unique id for section
  req_cxt?: ReqCxt;
}

export interface IShortlist {
  ref: IShortlistRef;
}

export const TEMPLATE_KEYS = ['standard_credential'] as const;

export type TEMPLATE_KEYS_TYPE = typeof TEMPLATE_KEYS[number];

export interface IAssignmentsCustomPreferences {
  isFieldsVisible?: {
    value: boolean;
  };
  // Zero-based rank for assignment text within one grouped card. Example
  // metadata input:
  // { director: { displayOrder: { value: 0 } },
  //   chair: { displayOrder: { value: 1 } } }
  // renders Director before Chair. If both displayOrder values are missing,
  // their existing real-ref/manual insertion order wins.
  displayOrder?: {
    value: number;
  };
};

export type IAssignmentFieldsOverride = Record<string, Partial<Record<AssignmentTableColumnsKeysType, string | number | undefined>>>;

// Base interface with fixed properties
export interface IBaseCredentialMetaData {
  active_template: TEMPLATE_KEYS_TYPE;
  // @Deprecated in favor of logo preferences assignment ref against s3 link of the uploaded img
  assignments_logo_override?: Record<string, string | undefined>;
  // assignment ref against an object of overridden column keys with their value
  assignment_fields_override?: IAssignmentFieldsOverride;
  // can be misc values where only UI state needs to be persisted
  assignments_custom_preferences?: Record<IShortlistRef, IAssignmentsCustomPreferences>;
  // manually created assignments
  assignments_manual?: Record<IShortlistRef, IAssigmentsTableDataWithRequiredIds>;
  // draggable, visibility...etc states of the assignment table
  assignments_table?: ITableColumns;
};

export interface ITemplateSortingState {
  value: 'asc' | 'desc' | 'manual' | null; // null means no sorting state, sort by natural order | manual is sorted by drag and drop
  sorted_by_field: AssignmentTableColumnsKeysType | null | 'manual'; // field which was sorted on, null if no sorting present or if was manually sorted by drag and drop
  updated_at_iso?: string;
  updated_at_unix_timestamp?: number;
};


// Assignments table - start
export type AssignmentTableColSortingState = 'asc' | 'desc' | null; // null means no sort state
// column fields which can be modified
export const EDITABLE_COLUMN_FIELDS: (keyof Pick<DefaultColsValue, 'title' | 'sortingState' | 'isVisible'>)[] = ['title', 'sortingState', 'isVisible'];

export type IGenerateDefaultColsData = (params: {
  defaultCols: DefaultCols;
  assignmentTableEdits: ITableColumns;
}) => ITableColumns;

export interface IColsValueName extends IDefaultColsValueMeta {
  value: string;
};

export interface IDefaultColsValueMeta {
  isModified: boolean; // if this flag is true then merge the user edited data else dont
  lastUpdatedBy?: string;
  lastUpdatedTs?: number;
};

export interface IColsValueIsVisible extends IDefaultColsValueMeta {
  value: boolean;
};

export interface ColsValueSortingState extends IDefaultColsValueMeta {
  value: AssignmentTableColSortingState;
};

export interface DefaultColsValue {
  title: IColsValueName;   // display name
  icon: string | null;
  isVisible: IColsValueIsVisible;
  isDummy: boolean; // last 2 columns at the end, placeholders to create new cols
  isHeaderNameVisible: boolean; // toggle visibility of column name, used for the 2 dummy cols at the end
  // add all the meta info about columns here
  isUserCreated: boolean;
  hasEditableCells: boolean;
  isFixedOrder: boolean;
  isModified: boolean;
  sortingState: ColsValueSortingState;
  lastUpdatedBy?: string;
  lastUpdatedTs?: number;
};

export type DefaultColsValueKeys = keyof DefaultColsValue;

// all features for columns will be controlled with flags
export type DefaultCols = Record<AssignmentTableColumnsKeysType, DefaultColsValue>;

export interface ITableColumns {
  columns: {
    by_id: DefaultCols;
    // can be used to maintain ordering
    all_ids: AssignmentTableColumnsKeysType[];
  },
};

// Assignments table - end

export type PresentationSlideTitle = {
  title_text?: string;
  repeat_on_all_slides?: boolean; // undefined can be used for indeterminate state
  updated_at_iso?: string;
  updated_at_unix_timestamp?: number;
};

export type PrivateAndConfidential = {
  placement?: 'bottom-left' | 'bottom-right' | null;
  updated_at_iso?: string;
  updated_at_unix_timestamp?: number;
};

export type CompanyOfficialLogo = {
  placement?: 'bottom-left' | 'bottom-right' | null;
  updated_at_iso?: string;
  updated_at_unix_timestamp?: number;
};

export type PresentationSlideNumber = {
  placement?: 'bottom-center';
  repeat_on_all_slides?: boolean; // undefined can be used for indeterminate state
  updated_at_iso?: string;
  updated_at_unix_timestamp?: number;
};

export type TileHeaderData = {
  colKey: AssignmentTableColumnsKeysType | null; // null means no header else the column key which is shown as header
}

export type CredentialTileSlotLayoutV1 = {
  version: 1;
  rows_per_slide: number;
  cols_per_row: number;
  slots: Array<Array<string | null>>;
};

export interface TEMPLATE_META_DATA {
  'css'?: {
    'display': string;
    'grid-template-columns': string;
    'grid-template-areas': string;
  },
  cols_per_row?: number;
  rows_per_slide?: number;
  tile_slot_layout?: CredentialTileSlotLayoutV1;
  // sorting state to show assignments in layout view
  sorting_state?: ITemplateSortingState;
  // this is Assignments Logo – if undefined then can assume it as true
  is_company_logo_visible?: boolean;
  tile_header_data?: TileHeaderData;
  tiles_alignment?: ALIGN_OPTION_VALUES;
  assignment_fields?: AssignmentTableColumnsKeysType[];
  //! this is deprecated in favor of presentation_slide_title since as a Object it is easier to work with than a primitive such as string if we need to add enhancements later
  slide_title?: string;
  presentation_slide_title?: PresentationSlideTitle;
  private_and_confidential?: PrivateAndConfidential;
  company_official_logo?: CompanyOfficialLogo;
  presentation_slide_number?: PresentationSlideNumber;
  slide_description?: string;
  company_logo?: string;
  // should be added only once
  created_at_iso?: string;
  created_at_unix_timestamp?: number;
  // should be updated every time
  updated_at_iso?: string;
  updated_at_unix_timestamp?: number;
}

// will have at-least one value if ppt was ever generated
export type TEMPLATE_META_DATA_KEYS = {
  [key in TEMPLATE_KEYS_TYPE]?: TEMPLATE_META_DATA;
};

export type ICredentialMetaData = IBaseCredentialMetaData & TEMPLATE_META_DATA_KEYS;


export interface IUseCreatedCredentialFields {
  name: string;
  client_company_name?: string;
  industry?: string[] | null;
  practice?: string;
  // assignment type is @deprecated - https://findemjira.atlassian.net/browse/RRINT-1839
  type?: string; // assignment type
  favorite?: boolean;
  capability?: string;
  libraries?: CredentialLibraryType[];
}

export enum CredentialLibraryType {
  CRED_LIBRARY = "CRED_LIBRARY",
  MARQUEE_LIBRARY = "MARQUEE_LIBRARY"
}

export interface ICredential extends IUseCreatedCredentialFields {
  _id: string;
  uid: string; // owner of the credential
  ref: CredRef;
  account_id: string;
  project: IProject;
  collaborators: ICollaborator[];
  requirements: IRequirement[];
  shortlist: IShortlist[];
  details: any;
  favorite_marked_by?: string[];
  // cred specific data e.g. ppt templates, layout etc...
  metadata?: ICredentialMetaData;
  created_at?: string;
  updated_at?: string;
  isDirty?: boolean;
  parent_id?: string;
}

export interface ICreateCredentialPayload extends IUseCreatedCredentialFields {
  name: string;
  joid: string;
  requirements?: IRequirement[];
}

export type UpdatedCredential = Partial<Omit<ICredential, 'ref' | 'project'>> & IUseCreatedCredentialFields & {
  ref: CredRef;
  project: {
    project_id: string;
  } | undefined;
};
export interface IUpdateCredentialPayload {
  joid: string;
  credential: UpdatedCredential;
}

export interface ICreateCredentialBody extends Omit<ICreateCredentialPayload, 'project_id'> {
  ref: undefined;
  project?: {
    project_id: string;
  };
}

export interface ICredentialResponseRaw {
  data: ICredential[]
}
export interface ICredentialResponse {
  data: ICredential;
}

export interface ICredentialsResponse {
  count: number;
  data: ICredential[];
}

export interface ILinkBeaconToCredPayload {
  ref: string;
  joid: string;
  project_id: string;
}

export interface IUnlinkBeaconToCredPayload {
  ref: CredRef;
  joid: string;
}

export interface ICredByRefPayload {
  ref: CredRef;
  joid: string;
}

export interface ICloneCredentialPayload {
  ref: CredRef;
  joid: string;
  name: string;
}


export enum ProjectScope {
  ALL = 'all', // user scope removed
  PERSONAL = 'personal', // default - user scope applied
}
export interface ISearchCredsPayload {
  page: number;
  size: number;
  filters?: ISearchFilter[];
  sort_fields?: ISearchSortFilter[];
  joid: string;
  activeTab: TabId;
}

export interface ICredHistoryPayload {
  ref: CredRef;
  joid: string;
}

export interface ISearchAssignmentsCredFilters {
  project_ref: CredRef;
  // if project_ref is provided and shortlist == undefined, then shortlist is picked from saved project, else from here (this is to handle scenarios where project is not saved yet)
  shortlist: IShortlist[];
  // if project_ref is provided and requirements == undefined, then requirements is picked from saved project, else from here (this is to handle scenarios where project is not saved yet)
  requirements: IRequirement[];
  // in case of search by assignment refs (e.g. in shortlist page)
  refs: AssignmentRef[];
}

export interface ISearchAssignmentsPayload extends Partial<ISearchAssignmentsCredFilters> {
  page: number;
  size: number;
  joid: string;
  search_id?: string | null;
}

export interface IGetAssignmentsPayload {
  page: number;
  size: number;
  joid: string;
  refs: AssignmentRef[];
  requirements?: IRequirement[];
}
export interface ICredBuilderFiltersACResp {
  count: number;
  data: {
    value: string;
  }[];
}
export interface ICredBuilderFiltersACPayload {
  field: string;
  prefix: string;
  joid: string;
}

export interface IDeleteCredentialResponse {
  data: {
    message: string;
    success: boolean;
  }
}

export interface IDeleteCredentialPayload {
  ref: CredRef;
  joid: string;
}

export interface ITransferCredentialOwnershipPayload {
  ref: CredRef;
  from_user: string;
  to_user: string;
  joid: string;
}

export interface ICredBuilderImgUploadResponse {
  message: string;
  key: string;
}

export type CREDENTIAL_IMG_UPLOAD_FIELDNAME = 'logo' | 'Key' | 'cred-company-logo';
export type CREDENTIAL_IMG_SOURCE = 'companyLogo' | 'assignmentLogoOverride';

export interface ICredBuilderImgUploadPayload {
  joid: string;
  formData: FormData;
  isAutoSave: boolean;
  // only for UI purposes - decides which metadata field to update
  source: CREDENTIAL_IMG_SOURCE;
  uniqueKey: string | undefined; // present in assignment logo override flow - source is assignmentLogoOverride. example assignmentRef, clientName
}

export interface IGetLinkedSearchDetailsPayload {
  joid: string;
  refs: string[];
}

export interface ILinkedSearchDetails {
  name: string;
  icp_id: string;
  ref: string;
  uid: string;
  created_at: string;
  updated_at: string;
}

export interface IGetLinkedSearchDetailsResponse {
  data: ILinkedSearchDetails[];
}

export interface IToggleFavoriteCredential {
  joid: string;
  ref: string;
};

export interface IToggleCredentialMarquee {
  joid: string;
  ref: string;
  library: NonNullable<ICredential['libraries']>;
}

export interface LogoPreference {
  is_default: boolean;
  logo_url: string;
  updated_ts: number;
}

export type ILogoPreferenceById = Record<string, LogoPreference[] | null>; // null if no logo preference exists for that id

export type IGetLogoPreferencesByIdsPayload = {
  joid: string;
  ids: string[];
};

export type IGetLogoPrerencesByIdsResponse = {
  data: ILogoPreferenceById;
};

export type IUploadLogoPreferencesResponse = {
  data: {
    account_id: string;
    client_company_id: string;
    logo_preferences: LogoPreference[];
  }
};

export type IUploadLogoPreferencesPayload = {
  joid: string;
  id: string;
  logoUrl: string;
};

export interface IUpdateDefaultLogoPreferenceResponse extends IUploadLogoPreferencesResponse {
  message: string;
};

export type IDeleteLogoPreferencesResponse = {
  data: {
    account_id: string;
    client_company_id: string;
    logo_preferences: string[];
  };
  message: string;
};

export type IDeleteLogoPreferencesPayload = {
  id: string;
  logos: string[];
  joid: string;
  force?: boolean; // will delete even if there wont be any remaining logo after the delete
};
